/**
 * MSW Multi-Store Locator — custom element
 *
 * Replaces the legacy jQuery `trademarkMap()` plugin (old `assets/custom.js` L46-280)
 * with a vanilla JS web component.
 *
 * Usage: `<msw-store-locator data-config='{"apiKey":"...","zoom":15,"markerColor":"#000",
 * "markerActiveColor":"#c00000","stores":[{"title":"...","mapAddress":"..."}]}' />`
 *
 * Loads Google Maps JS API on demand (deduped via window.__mswMapsPromise),
 * drops a custom SVG marker per store — using pre-set lat/lng when provided
 * (no Geocoding API call) and falling back to live geocoding of the address
 * string only when coords are blank — and wires list-row clicks to re-center
 * the map + change active marker state.
 */

const SILVER_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

// Marker SVG path lifted verbatim from old `assets/custom.js` L169.
const MARKER_PATH = 'M32.7374478,5.617 C29.1154478,1.995 24.2994478,0 19.1774478,0 C14.0544478,0 9.23944778,1.995 5.61744778,5.617 C-1.08555222,12.319 -1.91855222,24.929 3.81344778,32.569 L19.1774478,54.757 L34.5184478,32.6 C40.2734478,24.929 39.4404478,12.319 32.7374478,5.617 Z M19.3544478,26 C15.4954478,26 12.3544478,22.859 12.3544478,19 C12.3544478,15.141 15.4954478,12 19.3544478,12 C23.2134478,12 26.3544478,15.141 26.3544478,19 C26.3544478,22.859 23.2134478,26 19.3544478,26 Z';

const MARKER_ANCHOR = { x: 15, y: 55 };

// Default centre — Melbourne CBD; geocoder result for first store overrides.
const DEFAULT_CENTER = { lat: -37.8136, lng: 144.9631 };

function loadMapsApi(apiKey) {
  if (window.__mswMapsPromise) return window.__mswMapsPromise;
  window.__mswMapsPromise = new Promise((resolve, reject) => {
    const cbName = '__mswMapsCb' + Date.now();
    window[cbName] = () => {
      delete window[cbName];
      resolve();
    };
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&callback=${cbName}`;
    script.async = true;
    script.defer = true;
    script.onerror = (err) => {
      delete window[cbName];
      window.__mswMapsPromise = null;
      reject(err);
    };
    document.head.appendChild(script);
  });
  return window.__mswMapsPromise;
}

class MswStoreLocator extends HTMLElement {
  connectedCallback() {
    let cfg;
    try {
      cfg = JSON.parse(this.dataset.config || '{}');
    } catch (e) {
      console.warn('[msw-store-locator] invalid data-config JSON', e);
      return;
    }
    if (!cfg.apiKey || !Array.isArray(cfg.stores) || cfg.stores.length === 0) {
      console.info('[msw-store-locator] no apiKey or stores; skipping map init');
      this._wireRowClicksOnly();
      return;
    }
    this._cfg = cfg;
    this._markers = [];
    this._activeIndex = 0;

    loadMapsApi(cfg.apiKey)
      .then(() => this._initMap())
      .catch((err) => console.warn('[msw-store-locator] failed to load Maps API', err));
  }

  disconnectedCallback() {
    if (this._markers) {
      this._markers.forEach((m) => m && m.marker && m.marker.setMap(null));
    }
  }

  _wireRowClicksOnly() {
    // Even without a map we still want list interactivity (active row visual state).
    this.querySelectorAll('.msw-locator__row').forEach((row) => {
      row.addEventListener('click', () => {
        this.querySelectorAll('.msw-locator__row').forEach((r) => {
          r.classList.remove('is-active');
          r.setAttribute('aria-pressed', 'false');
        });
        row.classList.add('is-active');
        row.setAttribute('aria-pressed', 'true');
      });
    });
  }

  _initMap() {
    const mapEl = this.querySelector('.msw-locator__map[data-map]');
    if (!mapEl) return;

    this._map = new google.maps.Map(mapEl, {
      center: DEFAULT_CENTER,
      zoom: this._cfg.zoom || 15,
      draggable: false,
      clickableIcons: false,
      disableDefaultUI: true,
      styles: SILVER_MAP_STYLE,
    });

    this._cfg.stores.forEach((store, idx) => {
      // Prefer pre-set coordinates — no Geocoding API call (and no per-call cost).
      if (typeof store.lat === 'number' && typeof store.lng === 'number') {
        this._placeMarker(idx, new google.maps.LatLng(store.lat, store.lng));
        return;
      }
      // Fallback: live geocode the address string (billable, used only when lat/lng blank).
      if (!store.mapAddress) return;
      this._geocoder = this._geocoder || new google.maps.Geocoder();
      this._geocoder.geocode({ address: store.mapAddress }, (results, status) => {
        if (status !== 'OK' || !results || !results[0]) {
          console.warn(`[msw-store-locator] geocode failed for "${store.mapAddress}":`, status);
          return;
        }
        this._placeMarker(idx, results[0].geometry.location);
      });
    });

    // Wire row clicks to centre the map + update active state.
    this.querySelectorAll('.msw-locator__row').forEach((row) => {
      row.addEventListener('click', () => this._select(parseInt(row.dataset.storeIndex, 10)));
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._select(parseInt(row.dataset.storeIndex, 10));
        }
      });
    });
  }

  _placeMarker(idx, position) {
    const marker = new google.maps.Marker({
      map: this._map,
      position: position,
      icon: this._markerIcon(idx === this._activeIndex),
    });
    this._markers[idx] = { marker, position: position };
    if (idx === this._activeIndex) {
      this._map.setCenter(position);
    }
  }

  _markerIcon(active) {
    return {
      path: MARKER_PATH,
      fillColor: active ? this._cfg.markerActiveColor : this._cfg.markerColor,
      fillOpacity: 1,
      anchor: new google.maps.Point(MARKER_ANCHOR.x, MARKER_ANCHOR.y),
      strokeWeight: 0,
      scale: active ? 0.7 : 0.5,
    };
  }

  _select(idx) {
    if (Number.isNaN(idx)) return;
    this._activeIndex = idx;

    // Update DOM active state.
    this.querySelectorAll('.msw-locator__row').forEach((row) => {
      const rowIdx = parseInt(row.dataset.storeIndex, 10);
      const isActive = rowIdx === idx;
      row.classList.toggle('is-active', isActive);
      row.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    // Re-paint markers + recentre map.
    if (this._map && this._markers[idx] && this._markers[idx].position) {
      this._map.panTo(this._markers[idx].position);
    }
    this._markers.forEach((m, i) => {
      if (m && m.marker) m.marker.setIcon(this._markerIcon(i === idx));
    });
  }
}

if (!customElements.get('msw-store-locator')) {
  customElements.define('msw-store-locator', MswStoreLocator);
}
